import {
  LISTING_TEXT_REVISION_PROTOCOL,
  LISTING_REVISION_COMPLETION_PROTOCOL,
  normalizeRevisionAdviceText,
} from '../listing-draft-payload.js';

export function getRevisionAuditBlockingIssue(audit = {}) {
  const issues = Array.isArray(audit?.issues) ? audit.issues : [];
  const hardTypes = new Set(Array.isArray(audit?.hardRejectIssueTypes) ? audit.hardRejectIssueTypes : []);
  return issues.find((item) => hardTypes.has(item?.type)) || issues[0] || null;
}

export function isValidRevisionSearchTerms(searchTerms) {
  if (!searchTerms || typeof searchTerms !== 'object') return false;
  if (String(searchTerms.searchTermsEn || '').trim()) return true;
  return String(searchTerms.source || '') === 'listing_v4_code_finalizer'
    || Boolean(String(searchTerms.emptyReason || '').trim());
}

export function getInvalidRevisionChangedFields(normalized = {}, changedFields = []) {
  const changed = new Set(Array.isArray(changedFields) ? changedFields : []);
  const valid = {
    title: Boolean(normalized.title && (normalized.title.titleEn || normalized.title.titleOptions?.length)),
    bullets: Boolean(normalized.bullets && normalized.bullets.bulletsEn?.length),
    description: Boolean(
      normalized.description
      && Object.hasOwn(normalized.description, 'descriptionEn')
      && typeof normalized.description.descriptionEn === 'string'
    ),
    aplus: Boolean(normalized.description && Array.isArray(normalized.description.aplusModules)),
    searchTerms: isValidRevisionSearchTerms(normalized.searchTerms),
    qa: Array.isArray(normalized.qa),
  };
  return ['title', 'bullets', 'description', 'aplus', 'searchTerms', 'qa']
    .filter((field) => changed.has(field) && !valid[field]);
}

export function buildRevisionCandidateRecord({
  nextDraft,
  diffItems,
  audit,
  task = {},
  version = 1,
  basedOnVersion = 0,
} = {}) {
  return {
    draft: nextDraft,
    diffItems: Array.isArray(diffItems) ? diffItems : [],
    audit: audit || null,
    taskId: task.taskId || '',
    traceId: task.traceId || '',
    version: Number(version) || 1,
    basedOnVersion: Number(basedOnVersion) || 0,
    createdAt: new Date().toISOString(),
  };
}

export function mergeRevisionDraftByChangedFields({ normalized = {}, currentDraft = {}, changedFields = [] } = {}) {
  const changed = new Set(Array.isArray(changedFields) ? changedFields : []);
  const currentDescription = currentDraft.description || {};
  let description = currentDescription;
  if (changed.has('description') || changed.has('aplus')) {
    description = {
      ...currentDescription,
      ...(changed.has('description') ? {
        descriptionEn: normalized.description?.descriptionEn,
        descriptionZh: normalized.description?.descriptionZh,
      } : {}),
      ...(changed.has('aplus') ? {
        aplusModules: normalized.description?.aplusModules,
      } : {}),
    };
  }
  return {
    title: changed.has('title') ? normalized.title : currentDraft.title,
    bullets: changed.has('bullets') ? normalized.bullets : currentDraft.bullets,
    description,
    searchTerms: changed.has('searchTerms') ? normalized.searchTerms : currentDraft.searchTerms,
    qa: changed.has('qa') ? normalized.qa : currentDraft.qa,
  };
}

export function getTextRevisionWritebackDecision(mergeMeta = {}) {
  const revisionReview = mergeMeta?.revisionReview || {};
  const audit = mergeMeta?.audit || {};
  const semanticGate = revisionReview?.semanticGate
    || mergeMeta?.semanticGate
    || audit?.semanticGate
    || {};
  const isTextRevisionResult = mergeMeta?.revisionProtocol === LISTING_TEXT_REVISION_PROTOCOL
    || mergeMeta?.protocol === LISTING_TEXT_REVISION_PROTOCOL
    || mergeMeta?.generationMode === 'revision_text'
    || mergeMeta?.generationMode === 'listing_text_revision'
    || revisionReview?.protocol === LISTING_TEXT_REVISION_PROTOCOL;

  if (!isTextRevisionResult) {
    return {
      isTextRevisionResult: false,
      approved: false,
      writebackBlocked: false,
      status: '',
      reason: '',
      semanticGate,
      revisionReview,
    };
  }

  const reviewStatus = String(revisionReview?.status || '').trim().toLowerCase();
  const gateStatus = String(semanticGate?.status || '').trim().toLowerCase();
  const approvedReviewStatuses = new Set([
    'approved',
    'publishable',
    'corrected_and_publishable',
    'code_only_applied',
  ]);
  const hasExplicitGateDecision = semanticGate?.writebackApproved !== undefined
    || semanticGate?.approved !== undefined
    || semanticGate?.writebackBlocked !== undefined
    || Boolean(gateStatus);
  const gateApproved = semanticGate?.writebackApproved === true
    || semanticGate?.approved === true
    || gateStatus === 'approved';
  const explicitlyBlocked = semanticGate?.writebackBlocked === true
    || semanticGate?.writebackApproved === false
    || semanticGate?.approved === false
    || audit?.hardReject === true;
  const approved = !explicitlyBlocked && (
    hasExplicitGateDecision
      ? gateApproved
      : approvedReviewStatuses.has(reviewStatus)
  );
  const writebackBlocked = explicitlyBlocked || !approved;
  const hardRejectReason = audit?.hardReject
    ? getRevisionAuditBlockingIssue(audit)?.reason
    : '';
  const reason = normalizeRevisionAdviceText(
    semanticGate?.blockReason
      || revisionReview?.blockReason
      || revisionReview?.reason
      || revisionReview?.error
      || hardRejectReason
      || (reviewStatus ? `整稿复检状态为 ${reviewStatus}` : '')
      || '后端未返回明确的整稿复检批准结论',
  );

  return {
    isTextRevisionResult: true,
    approved,
    writebackBlocked,
    status: reviewStatus || gateStatus || 'not_approved',
    reason,
    semanticGate,
    revisionReview,
  };
}

export function buildRevisionWritebackMeta(meta = {}, task = {}, execution = {}) {
  const mergeMeta = meta?.mergeMeta || {};
  const revisionReview = mergeMeta.revisionReview || {};
  const functionTrace = mergeMeta.functionTrace || null;
  const taskId = task?.taskId || execution?.taskId || '';
  const traceId = task?.traceId || execution?.traceId || functionTrace?.traceId || '';
  const lastRevisionExecution = {
    schemaVersion: 'listing_text_revision_execution_ref.v1',
    protocol: LISTING_TEXT_REVISION_PROTOCOL,
    status: revisionReview.status || 'applied',
    taskId,
    traceId,
    finishedAt: revisionReview.finishedAt || functionTrace?.finishedAt || new Date().toISOString(),
    reviewText: normalizeRevisionAdviceText(revisionReview.reviewText || revisionReview.summary || ''),
    recheckText: normalizeRevisionAdviceText(revisionReview.recheckText || revisionReview.recheckSummary || ''),
  };
  const scopeKey = String(execution?.revisionCompletion?.scopeKey || '').trim();
  const revisionCompletion = execution?.revisionCompletion?.approved === true && scopeKey
    ? {
        schemaVersion: LISTING_REVISION_COMPLETION_PROTOCOL,
        status: 'approved',
        scopeKey,
        revisionStatus: execution.revisionCompletion.status || revisionReview.status || 'approved',
        taskId,
        traceId,
        completedAt: lastRevisionExecution.finishedAt,
      }
    : null;
  return {
    ...meta,
    mergeMeta: {
      ...mergeMeta,
      generationMode: mergeMeta.generationMode || 'revision_text',
      revisionProtocol: LISTING_TEXT_REVISION_PROTOCOL,
      changedFields: Array.isArray(execution?.changedFields)
        ? [...execution.changedFields]
        : (Array.isArray(mergeMeta.changedFields) ? [...mergeMeta.changedFields] : []),
      lastRevisionExecution,
      ...(revisionCompletion ? { revisionCompletion } : {}),
    },
  };
}
